import React, { useState } from "react";
import { useRive } from "@rive-app/react-canvas";
import { styled } from "@mui/system";
import BeegruButton from "../../common-components/buttons/BeegruButton";
import { updateFeatureDiscoveryTrackerThunk } from "../../../redux-magic/store";
import { ObjectId } from "bson";
import StateInterface from "../../../redux-magic/state-interface";
import ReplayIcon from "@mui/icons-material/Replay";
import CampaignIcon from "@mui/icons-material/Campaign";
import { NextRouter } from "next/router";

const Container = styled("div")({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
	padding: "1rem",
});

const RiveFullScreenContainer = styled("div")({
	position: "absolute",
	top: 0,
	left: 0,
	width: "100%",
	height: "95%",
	zIndex: 0,
	overflow: "hidden",
	"& canvas": {
		width: "100% !important",
		height: "100% !important",
		display: "block",
	},
});

const MessageContainer = styled("div")<{ flipping: boolean }>(({ flipping }) => ({
	fontSize: "1.5rem",
	position: "relative",
	top: "3rem",
	padding: "1rem",
	zIndex: 1,
	textAlign: "center",
	width: "100%",
	fontWeight: "500",
	perspective: "600px",
	transition: "none",
	"& .flip": {
		display: "inline-block",
		transition: "transform 0.6s cubic-bezier(.68,-0.55,.27,1.55)",
		transform: flipping ? "rotateX(180deg)" : "rotateX(0deg)",
		backfaceVisibility: "hidden",
	},
}));

export default function BroadcastTutorial({
	setPage,
	broadcastFeatureDiscoveryTracker,
	profile_context,
	dispatch,
	router,
}: {
	setPage: Function;
	broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
	profile_context: {
		user_id: string | ObjectId;
		is_business_profile: boolean;
		business_profile_id: string | ObjectId;
	};
	dispatch: Function;
	router: NextRouter;
}) {
	const [message, setMessage] = useState("Send your property offer to 3500+ agents in one click");
	const [flipping, setFlipping] = useState(false);
	const [tutorialComplete, setTutorialComplete] = useState(false);

	const localLang = {
		skip: router.locale === "ar-AE" ? "تخطي" : router.locale === "kn-IN" ? "ಬಿಟ್ಟುಹೋಗಿ" : "Skip",
		replay: router.locale === "ar-AE" ? "إعادة التشغيل" : router.locale === "kn-IN" ? "ಮರುಪ್ರಸಾರ" : "Replay",
		start_broadcast:
			router.locale === "ar-AE" ? "بدء البث" : router.locale === "kn-IN" ? "ಪ್ರಸಾರವನ್ನು ಪ್ರಾರಂಭಿಸಿ" : "Start Broadcast",
	};

	// Helper to flip on message change
	React.useEffect(() => {
		if (!flipping) return;
		const timer = setTimeout(() => setFlipping(false), 600);
		return () => clearTimeout(timer);
	}, [flipping]);

	// Change message and trigger flip
	React.useEffect(() => {}, []);

	React.useEffect(() => {
		const message1 = setTimeout(() => {
			setFlipping(true);
			setTimeout(() => setMessage("Only agents who're interested will reply directly"), 300);
		}, 3500);

		const message2 = setTimeout(() => {
			setFlipping(true);
			setTimeout(() => setMessage("Close deals faster, let agents do the outreach for you"), 300);
		}, 6000);

		const message3 = setTimeout(() => {
			setTutorialComplete(true);
		}, 8000);

		return () => {
			clearTimeout(message1);
			clearTimeout(message2);
			clearTimeout(message3);
		};
	}, []);

	const { RiveComponent, rive } = useRive({
		src: "/images/broadcast/broadcastIntro.riv",
		autoplay: true,
	});

	const handleSkippedTutorial = (e: any) => {
		e.stopPropagation();
		if (!broadcastFeatureDiscoveryTracker.viewed) {
			dispatch(
				updateFeatureDiscoveryTrackerThunk({
					profile_id: profile_context.is_business_profile
						? profile_context.business_profile_id
						: profile_context.user_id,
					is_business_profile: profile_context.is_business_profile,
					broadcast_skipped: true,
				}),
			);
		}
		setPage(2);
	};

	const handleReplay = () => {
		if (rive) {
			rive.reset();
			rive.play();
		}
		// Optionally reset message and animation state if needed:
		setMessage("Send your property to 3500+ agents in one click");
		setTutorialComplete(false);
		setFlipping(false);
		// Restart message sequence

		setTimeout(() => {
			setFlipping(true);
			setTimeout(() => setMessage("Only agents who're interested will reply directly"), 300);
		}, 3500);

		setTimeout(() => {
			setFlipping(true);
			setTimeout(() => setMessage("Close deals faster, let agents do the outreach for you"), 300);
			setTimeout(() => setTutorialComplete(true), 2000);
		}, 6000);
	};

	return (
		<Container>
			{!tutorialComplete && (
				<BeegruButton
					variant="text"
					flavour="intro"
					onClick={handleSkippedTutorial}
					sx={{ position: "absolute", top: "1rem", right: "1rem", zIndex: 1 }}
				>
					{localLang.skip}
				</BeegruButton>
			)}
			<MessageContainer flipping={flipping}>
				<span className="flip">{message}</span>
			</MessageContainer>
			<RiveFullScreenContainer>
				<RiveComponent style={{ width: "100%", height: "100%" }} />
			</RiveFullScreenContainer>
			{tutorialComplete && (
				<div
					style={{
						display: "flex",
						gap: "1rem",
						position: "absolute",
						left: 0,
						bottom: "0rem",
						width: "100%",
						padding: "0 1rem",
					}}
				>
					<BeegruButton
						size="large"
						variant="text"
						fullWidth
						disableRipple
						disableFocusRipple
						disableTouchRipple
						onClick={handleReplay}
						sx={{
							"&.MuiButton-text": {
								color: "#621b16",
								background: "none",
								"&:hover": {
									background: "none",
								},
								"&:active": {
									background: "none",
								},
								"&:focus": {
									background: "none",
								},
							},
						}}
					>
						<ReplayIcon sx={{ mr: 1 }} />
						{localLang.replay}
					</BeegruButton>
					<BeegruButton
						size="large"
						flavor="primary"
						variant="contained"
						fullWidth
						disableRipple
						disableFocusRipple
						disableTouchRipple
						onClick={() => {
							if (!broadcastFeatureDiscoveryTracker.viewed) {
								dispatch(
									updateFeatureDiscoveryTrackerThunk({
										profile_id: profile_context.is_business_profile
											? profile_context.business_profile_id
											: profile_context.user_id,
										is_business_profile: profile_context.is_business_profile,
										broadcast_skipped: true,
									}),
								);
							}
							setPage(2);
						}}
						sx={{
							"&.MuiButton-contained": {
								background: "#621b16",
								color: "#FFFFFF",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								"&:hover": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
								"&:active": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
								"&:focus": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
							},
						}}
					>
						<CampaignIcon sx={{ mr: 1 }} />
						{localLang.start_broadcast}
					</BeegruButton>
				</div>
			)}
		</Container>
	);
}
